import React, { useRef, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import ProfilePrintContent from "./profilePrintContent";
import { Printer, X } from "lucide-react";

export default function ProfileViewPage({
  expertID,
  projectID,
  profile,
  setIsProfileView,
  projectType,
  clickingOnProfile,
  markProfilesAsCallRequested,
}) {
  const printRef = useRef(null);

	useEffect(() => {
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = "";
		};
	}, []);

  const getPrintableTitle = () =>
    profile?.isShareName
      ? `${projectType === "exp" ? "EPH Expert" : "EPH Consultant"} - Profile`
      : `${projectType === "exp" ? "EPH_EXP_" : "EPH_CON_"}${profile?.expertDigitalID || ""} - Profile`;

  const copyDocumentStylesToPrintWindow = (printWindow) => {
    const styleNodes = Array.from(
      document.querySelectorAll('style, link[rel="stylesheet"]')
    );

    styleNodes.forEach((node) => {
      try {
        printWindow.document.head.appendChild(node.cloneNode(true));
      } catch (error) {
        console.error("Failed to copy stylesheet to print window:", error);
      }
    });
  };

  const waitForImages = async (doc) => {
    const images = Array.from(doc.images || []);
    await Promise.all(
      images.map((img) => {
        if (img.complete) return Promise.resolve();
        return new Promise((resolve) => {
          img.onload = resolve;
          img.onerror = resolve;
        });
      })
    );
  };

  const handlePrint = async () => {
    const printableNode = printRef.current;
    if (!printableNode) return;

    const printWindow = window.open("", "_blank", "width=1200,height=900");
    if (!printWindow) return;

    const title = getPrintableTitle();

    printWindow.document.open();
    printWindow.document.write(`
      <!DOCTYPE html>
      <html lang="en">
        <head>
          <meta charset="UTF-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />
          <title>${title}</title>
        </head>
        <body>
          <div id="print-root"></div>
        </body>
      </html>
    `);
    printWindow.document.close();

    copyDocumentStylesToPrintWindow(printWindow);

    const printStyle = printWindow.document.createElement("style");
    printStyle.textContent = `
      @page {
        size: A4;
        margin: 12mm;
      }

      html, body {
        background: #ffffff !important;
        margin: 0 !important;
        padding: 0 !important;
        overflow: visible !important;
        height: auto !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      * {
        box-sizing: border-box;
      }

      body {
        font-family: Arial, Helvetica, sans-serif;
      }

      img {
        max-width: 100%;
      }

      #print-root {
        width: 100%;
        margin: 0;
        padding: 0 2mm;
        background: #ffffff;
      }

      // .no-print {
      //   display: none !important;
      // }

      .print-hide-bubbles {
        display: none !important;
      }

      .print-root {
        position: static !important;
        width: 100% !important;
        margin: 0 !important;
        padding: 0 !important;
        background: #ffffff !important;
        overflow: visible !important;
        height: auto !important;
        box-shadow: none !important;
      }

      .print-no-shadow {
        box-shadow: none !important;
      }

      .print-border {
        border: 1px solid #e5e7eb !important;
      }

      .print-single-column {
        display: block !important;
      }

      .print-single-column > * {
        width: 100% !important;
        max-width: 100% !important;
      }

      .print-stack-gap > *:not(:last-child) {
        margin-bottom: 16px !important;
      }

      .print-gap-reset {
        gap: 16px !important;
      }

      .print-mt-reset {
        margin-top: -85px !important;
      }

      .print-pt-reset {
        padding-top: 24px !important;
      }

      .print-px-reset {
        padding-left: 0 !important;
        padding-right: 0 !important;
      }

      .print-rounded-none {
        border-radius: 0 !important;
      }

      .print-range {
        display: none !important;
      }

      .print-score-bar-wrap {
        display: block !important;
        flex: 1;
      }

      .print-score-bar-bg {
        height: 8px;
        width: 100%;
        background: #e5e7eb;
        border-radius: 999px;
        overflow: hidden;
      }

      .print-score-bar-fill {
        height: 8px;
        background: #365561;
        border-radius: 999px;
      }

      .print-banner {
        height: 130px !important;
        background-size: cover !important;
        background-position: top !important;
      }

      .print-profile-image {
        width: 128px !important;
        height: 128px !important;
      }

      .print-eph-logo {
        width: 150px;
      }

      .print-text-sm {
        font-size: 12px !important;
        line-height: 1.5 !important;
      }

      .print-text-md {
        font-size: 14px !important;
        line-height: 1.6 !important;
      }

      .print-text-lg {
        font-size: 20px !important;
      }

      .print-text-xl {
        font-size: 24px !important;
      }

      .print-header-row {
        display: flex !important;
        flex-direction: row !important;
        justify-content: space-between !important;
        align-items: flex-start !important;
        gap: 16px !important;
        flex-wrap: nowrap !important;
      }

      .print-header-left {
        flex: 1 1 auto !important;
        min-width: 0 !important;
      }

      .print-rate-wrap {
        display: flex !important;
        justify-content: flex-end !important;
        align-items: flex-start !important;
        flex: 0 0 auto !important;
        margin-bottom: 0 !important;
      }

      .print-rate-card {
        min-width: 220px !important;
      }

      .print-card-outer {
        break-inside: avoid;
        page-break-inside: avoid;
      }

      .print-experience-split,
      .print-experience-list {
        break-inside: auto !important;
        page-break-inside: auto !important;
      }

      .print-experience-item {
        break-inside: avoid;
        page-break-inside: avoid;
      }

      .print-safe-reset {
        transform: none !important;
        animation: none !important;
        transition: none !important;
      }

      .print-info-side {
        padding-left: 6.5mm !important;
      }

      .print-rate-side {
        padding-right: 6mm !important;
      }

      .print-profile-side {
        padding-left: 5mm !important;
      }

      .print-availability {
        display: flex !important;
        flex-direction: column !important;
        justify-content: space-between !important;
        align-items: flex-start !important;
        gap: 5px !important;
        flex-wrap: nowrap !important;
        padding-left: 25px;
      }

      .slot-selected-overlay {
        position: absolute;
        inset: 0;
        z-index: 5;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(54, 85, 97, 0.18);
        pointer-events: none;
      }

      .slot-selected-check {
        width: 28px;
        height: 28px;
        border-radius: 999px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #365561;
        color: #ffffff;
        font-size: 18px;
        line-height: 1;
        font-weight: 800;
        box-shadow: 0 4px 10px rgba(15, 23, 42, 0.18);
      }

      @media print {
        html, body {
          background: #ffffff !important;
          margin: 0 !important;
          padding: 0 !important;
          overflow: visible !important;
          height: auto !important;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }

        .no-print {
          display: none !important;
        }

        .print-availability {
          display: flex !important;
          flex-direction: column !important;
          align-items: flex-start !important;
          gap: 5px !important;
          padding-left: 25px !important;
          width: 100% !important;
          background: #ffffff !important;
          box-shadow: none !important;
        }

        .availability-title {
          padding-top: 0 !important;
          font-size: 12px !important;
          line-height: 1.4 !important;
          color: #6b7280 !important;
          background: #ffffff !important;
        }

        .availability-grid {
          display: grid !important;
          grid-template-columns: repeat(auto-fill, 85px) !important;
          gap: 6px !important;
          width: 100% !important;
          background: #ffffff !important;
          box-shadow: none !important;
          filter: none !important;
          -webkit-filter: none !important;
        }

        .availability-card {
          width: 85px !important;
          height: 82px !important;
          min-width: 85px !important;
          max-width: 85px !important;
          overflow: hidden !important;
          border-radius: 12px !important;
          border: 1px solid #d1d5db !important;
          background: rgb(54 85 97 / 0.05) !important;
          box-shadow: none !important;
          filter: none !important;
          -webkit-filter: none !important;
          break-inside: avoid !important;
          page-break-inside: avoid !important;
        }

        .availability-card-top {
          background: #365561 !important;
          color: #ffffff !important;
          padding: 4px 4px !important;
          text-align: center !important;
          font-size: 10px !important;
          line-height: 1.2 !important;
          font-weight: 600 !important;
          text-transform: uppercase !important;
          letter-spacing: 0.04em !important;
        }

        .availability-card-body {
          padding: 4px 8px !important;
          text-align: center !important;
        }

        .availability-day {
          font-size: 16px !important;
          line-height: 1 !important;
          font-weight: 700 !important;
          color: #365561 !important;
        }

        .availability-weekday {
          margin-top: 2px !important;
          font-size: 10px !important;
          line-height: 1.2 !important;
          font-weight: 600 !important;
          color: #374151 !important;
        }

        .availability-time {
          margin-top: 2px !important;
          overflow: hidden !important;
          text-overflow: ellipsis !important;
          white-space: nowrap !important;
          font-size: 10px !important;
          line-height: 1.2 !important;
          color: #6b7280 !important;
        }
      }
    `;
    printWindow.document.head.appendChild(printStyle);

    const clonedNode = printableNode.cloneNode(true);

    clonedNode
      .querySelectorAll("[data-print-hide='true'], .no-print")
      .forEach((node) => node.remove());

    const mountPoint = printWindow.document.getElementById("print-root");
    mountPoint.appendChild(clonedNode);

    await waitForImages(printWindow.document);

    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const actionButtonStyle = {
    border: "none",
    borderRadius: "999px",
    padding: "10px 16px",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.2s ease",
    outline: "none",
  };

  return (
    <>
      <style>
        {`
          @media print {
            .no-print {
              display: none !important;
            }
          }
        `}
      </style>

      <AnimatePresence>
        <motion.div
          className="fixed inset-0 z-50 bg-gray-200/70 p-4 backdrop-blur-[6px]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setIsProfileView(false)}
        >
          <div
            className="bg-transparent shadow-none w-full h-[calc(100vh-100px)] top-16 overflow-auto relative rounded-xl"
            onClick={(e) => e.stopPropagation()}
          >
						<div className="no-print sticky top-2 max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 flex justify-end gap-6 z-50">
							<div className="absolute right-10 flex flex-end gap-4">
								<button
									type="button"
									onClick={handlePrint}
									className="relative group flex items-center justify-center h-16 w-16 bg-none border-[3px] border-slate-500 rounded-full focus:outline-none"
								>
									<div className="h-12 w-12 bg-white border-[1px] border-gray-200 rounded-full transition-transform duration-200 hover:scale-110 flex items-center justify-center">
										<svg width="64px" height="64px" viewBox="-14 -10 60.00 60.00" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="#000000" stroke-width="0.0004">
											<g id="SVGRepo_bgCarrier" stroke-width="0"></g>
											<g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round" stroke="#CCCCCC" stroke-width="0.08"></g>
											<g id="SVGRepo_iconCarrier"> 
												<path 
													d="M25.6686 26.0962C25.1812 26.2401 24.4656 26.2563 23.6984 26.145C22.875 26.0256 22.0351 25.7739 21.2096 25.403C22.6817 25.1888 23.8237 25.2548 24.8005 25.6009C25.0319 25.6829 25.412 25.9021 
														25.6686 26.0962ZM17.4552 24.7459C17.3953 24.7622 17.3363 24.7776 17.2776 24.7939C16.8815 24.9017 16.4961 25.0069 16.1247 25.1005L15.6239 25.2275C14.6165 25.4824 13.5865 25.7428 12.5692 26.0529C12.9558 
														25.1206 13.315 24.178 13.6667 23.2564C13.9271 22.5742 14.193 21.8773 14.468 21.1894C14.6075 21.4198 14.7531 21.6503 14.9046 21.8814C15.5948 22.9326 16.4624 23.9045 17.4552 24.7459ZM14.8927 
														14.2326C14.958 15.383 14.7098 16.4897 14.3457 17.5514C13.8972 16.2386 13.6882 14.7889 14.2489 13.6185C14.3927 13.3185 14.5105 13.1581 14.5869 13.0744C14.7049 13.2566 14.8601 13.6642 14.8927 
														14.2326ZM9.63347 28.8054C9.38148 29.2562 9.12426 29.6782 8.86063 30.0767C8.22442 31.0355 7.18393 32.0621 6.64941 32.0621C6.59681 32.0621 6.53316 32.0536 6.44015 31.9554C6.38028 31.8926 6.37069 
														31.8476 6.37359 31.7862C6.39161 31.4337 6.85867 30.8059 7.53527 30.2238C8.14939 29.6957 8.84352 29.2262 9.63347 28.8054ZM27.3706 26.1461C27.2889 24.9719 25.3123 24.2186 25.2928 24.2116C24.5287 
														23.9407 23.6986 23.8091 22.7552 23.8091C21.7453 23.8091 20.6565 23.9552 19.2582 24.2819C18.014 23.3999 16.9392 22.2957 16.1362 21.0733C15.7816 20.5332 15.4628 19.9941 15.1849 19.4675C15.8633 17.8454 
														16.4742 16.1013 16.3632 14.1479C16.2737 12.5816 15.5674 11.5295 14.6069 11.5295C13.948 11.5295 13.3807 12.0175 12.9194 12.9813C12.0965 14.6987 12.3128 16.8962 13.562 19.5184C13.1121 20.5751 12.6941 
														21.6706 12.2895 22.7311C11.7861 24.0498 11.2674 25.4103 10.6828 26.7045C9.04334 27.3532 7.69648 28.1399 6.57402 29.1057C5.8387 29.7373 4.95223 30.7028 4.90163 31.7107C4.87693 32.1854 5.03969 32.6207 
														5.37044 32.9695C5.72183 33.3398 6.16329 33.5348 6.6487 33.5354C8.25189 33.5354 9.79489 31.3327 10.0876 30.8909C10.6767 30.0029 11.2281 29.0124 11.7684 27.8699C13.1292 27.3781 14.5794 27.011 15.985 
														26.6562L16.4884 26.5283C16.8668 26.4321 17.2601 26.3257 17.6635 26.2153C18.0904 26.0999 18.5296 25.9802 18.976 25.8665C20.4193 26.7844 21.9714 27.3831 23.4851 27.6028C24.7601 27.7883 25.8924 27.6807 
														26.6589 27.2811C27.3486 26.9219 27.3866 26.3676 27.3706 26.1461ZM30.4755 36.2428C30.4755 38.3932 28.5802 38.5258 28.1978 38.5301H3.74486C1.60224 38.5301 1.47322 36.6218 1.46913 36.2428L1.46884 
														3.75642C1.46884 1.6039 3.36763 1.4734 3.74457 1.46908H20.263L20.2718 1.4778V7.92396C20.2718 9.21763 21.0539 11.6669 24.0158 11.6669H30.4203L30.4753 11.7218L30.4755 36.2428ZM28.9572 
														10.1976H24.0169C21.8749 10.1976 21.7453 8.29969 21.7424 7.92417V2.95307L28.9572 10.1976ZM31.9447 36.2428V11.1157L21.7424 0.871022V0.823357H21.6936L20.8742 0H3.74491C2.44954 0 0 0.785336 0 
														3.75711V36.2435C0 37.5427 0.782956 40 3.74491 40H28.2001C29.4952 39.9997 31.9447 39.2143 31.9447 36.2428Z" 
														fill="#EB5757"
													>
												</path> 
											</g>
										</svg>
									</div>

									<div className="
										absolute top-full mt-2.5
										opacity-0 group-hover:opacity-100
										transition-opacity duration-200
										pointer-events-none
										flex flex-col items-center
									">
										<div className="w-2 h-2 bg-[#365561] rotate-45 absolute -top-1"></div>
										<div className="bg-[#365561] text-white text-xs px-3 py-2 rounded-lg shadow-lg whitespace-nowrap">
											Print or save PDF
										</div>
									</div>
								</button>

								<button
									type="button"
									onClick={() => setIsProfileView(false)}
									className="relative flex items-center justify-center h-16 w-16 bg-none border-[3px] border-slate-500 rounded-full focus:outline-none"
								>
									<div className="h-12 w-12 bg-white border-[1px] border-gray-200 rounded-full transition-transform duration-200 hover:scale-110 flex items-center justify-center">
										<X size={20} />
									</div>
								</button>
							</div>
						</div>

            <ProfilePrintContent
              ref={printRef}
              expertID={expertID}
              projectID={projectID}
              profile={profile}
              projectType={projectType}
							renderScope="single-view"
              clickingOnProfile={clickingOnProfile}
              markProfilesAsCallRequested={markProfilesAsCallRequested}
            />
          </div>
        </motion.div>
      </AnimatePresence>
    </>
  );
}